import * as React from "react";

import { Box, Typography } from "@material-ui/core";
import { useMutation, useQuery } from "react-query";
import * as Redux from "react-redux";

import { OnboardingStatusContext } from "client/context";
import {
  OnboardingStatusToCurrentStateMap,
  Status,
  StatusLabel,
  StatusMap,
  StatusStep,
  useStyles,
} from "client/domain/OnboardingStatus";
import {
  useBranding,
  useMerchantModel,
  useOnboardingStatusContext,
  useTranslation,
} from "client/hooks";
import { OnboardingStatus } from "client/lib/apt-merchant";
import { UserService, ZdoService } from "client/services";
import { errorActions, formCompletionActions } from "client/store";
import { RestoreResponse } from "client/types";

import { General } from "./General";
import { Shopify } from "./Shopify";
import { WooCommerce } from "./WooCommerce";

const onboardingStatusToCurrentStateMap: OnboardingStatusToCurrentStateMap = {
  INELIGIBLE: "Declined",
  NO_RECORD: "None",
  IN_PROGRESS: "None",
  IN_REVIEW: "None",
  REQUIRES_ATTENTION: "Paused",
  READY_TO_INSTALL: "InProgress",
  COMPLETE: "Done",
};

export type IntegrationStepInput = Pick<
  OnboardingStatusContext,
  "merchantId" | "onboardingGroup" | "onboardingStatus"
>;

export const determineCurrentState = ({
  merchantId,
  onboardingGroup,
  onboardingStatus,
}: IntegrationStepInput): Status => {
  if (
    onboardingStatus === "IN_REVIEW" &&
    (onboardingGroup === "ZDO Merchant" ||
      onboardingGroup === "ZDO Pilot V1" ||
      Boolean(merchantId))
  ) {
    return "InProgress";
  }

  if (onboardingStatus === "INELIGIBLE") {
    return "Declined";
  }

  if (onboardingStatus === "REQUIRES_ATTENTION") {
    return "Paused";
  }

  return onboardingStatusToCurrentStateMap[
    onboardingStatus as OnboardingStatus
  ];
};

export const IntegrationStep: React.FC<{
  credentials?: ZdoService.Credentials;
}> = ({ credentials }) => {
  const [gatewayEnabled, setGatewayEnabled] = React.useState(false);
  const t = useTranslation();
  const { brandTitle } = useBranding();
  const dispatch = Redux.useDispatch();

  const {
    onboardingGroup,
    onboardingStatus,
    merchantId,
  } = useOnboardingStatusContext();

  const { merchant, setMerchant } = useMerchantModel();
  const classes = useStyles();
  const [currentState, setCurrentState] = React.useState<Status>(
    determineCurrentState({
      merchantId,
      onboardingGroup,
      onboardingStatus,
    })
  );

  const { mutate: enableGateway } = useMutation(
    async (enabled: boolean) => {
      const result = await UserService.updateGatewayEnabled(enabled);
      setGatewayEnabled(enabled);
      return result;
    },
    {
      onSuccess: ({ formCompletedState }) => {
        if (formCompletedState) {
          dispatch(formCompletionActions.setFormCompletion(formCompletedState));
        }
      },
    }
  );

  useQuery<RestoreResponse, Error>("restore", UserService.restoreUser, {
    enabled: gatewayEnabled,
    onSuccess: (response) => {
      setMerchant(response);
    },
    onError(err) {
      dispatch(errorActions.setError(err.message));
    },
  });

  React.useEffect(() => {
    setCurrentState(
      determineCurrentState({
        merchantId,
        onboardingGroup,
        onboardingStatus,
      })
    );
  }, [merchantId, onboardingGroup, onboardingStatus]);

  let Integration = General;

  if (merchant.storeDetails.platform.value === "Shopify") {
    Integration = Shopify;
  }
  if (merchant.storeDetails.platform.value === "WooCommerce") {
    Integration = WooCommerce;
  }

  const statusMap: StatusMap = {
    Done: {
      className: classes.dark,
    },
    InProgress: {
      className: classes.light,
    },
    None: {
      className: classes.light,
    },
    Paused: {
      className: classes.light,
      text: t("uiCompleteAfterComplianceReview"),
    },
    Reviewing: {},
    RequiresAttention: {},
    Declined: {
      className: classes.light,
      text: t("uiStatusIntegrationDeclinedSubheading", {
        brandTitle: brandTitle,
      }),
    },
  };
  const { className, text } = statusMap[currentState];

  return (
    <StatusStep
      timeToFade={750}
      activeOnboardingStep="Integration"
      stepStatus={currentState}
    >
      <Box className={`${className} ${classes.statusWrapper}`} mb={2} p={2}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>{t("uiAddBrandToCheckout", { brandTitle })}</strong>
          </Typography>
          <StatusLabel status={currentState} />
        </Box>
        {text && (
          <Box mt={1}>
            <Typography variant="body2">{text}</Typography>
          </Box>
        )}
        {currentState === "InProgress" && (
          <Box mt={1.5}>
            <Integration
              merchantId={credentials?.merchantId ?? ""}
              secretKey={credentials?.secretKey ?? ""}
              handleClickVerifyGatewayEnabled={() => enableGateway(true)}
            />
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
